import { autoinject, transient } from "aurelia-dependency-injection";
import { StringHelper } from "../../helpers/string.helper";
import { CompTextBox } from "./comp-text-box";

@autoinject
@transient()
export class InputFormatterValueConverter {

    public isNegative(n) {
        return ((n = +n) || 1 / n) < 0;
    }

    public getSign(value) {
        if (this.isNegative(value)) {
            return "-";
        } else {
            const firstChar: string = value && value.length > 0 ? value[0] : "";
            return firstChar == "+" || firstChar == "-" ? firstChar : "";
        }
    }

    public repeat(text: string, count: number): string {
        "use strict";
        if (text == null) {
            throw new TypeError("can't convert " + text + " to object");
        }
        let str = "" + text;
        count = +count;
        if (count !== count) {
            count = 0;
        }
        if (count < 0) {
            throw new RangeError("repeat count must be non-negative");
        }
        if (count === Infinity) {
            throw new RangeError("repeat count must be less than infinity");
        }
        count = Math.floor(count);
        if (str.length === 0 || count === 0) {
            return "";
        }
        // ensuring count is a 31-bit integer allows us to heavily optimize the
        // main part. But anyway, most current (August 2014) browsers can't handle
        // strings 1 << 28 chars or longer, so:
        if (str.length * count >= 1 << 28) {
            throw new RangeError("repeat count must not overflow maximum string size");
        }
        let rpt = "";
        for (; ;) {
            if ((count & 1) == 1) {
                rpt += str;
            }
            count >>>= 1;
            if (count == 0) {
                break;
            }
            str += str;
        }
        // could we try:
        // return Array(count + 1).join(text);
        return rpt;
    }

    public toView(value: any | string, param: any): string {

        const textBox: CompTextBox = param.vm as CompTextBox;

        if (value == null || value == undefined) {
            value = "";
        }

        const sign = this.getSign(value);

        if (typeof value == 'number') {
            value = value.toString();
        }

        if ((sign == "+" || sign == "-") && value[0] == sign && ["double", "float", "integer", "digits"].indexOf(textBox.type) > -1) {
            value = value.substr(1);
        }

        if (textBox) {
            switch (textBox.type) {
                case "double":
                case "float":
                    {
                        if (value && value.toString() == "NaN") {
                            value = "";
                        }
                        if (value && textBox.input && textBox.input.value) {
                            // values will look like 13.4 (the dot separator is from ECMA Specification so OS indipendent)
                            // https://tc39.es/ecma262/#sec-static-semantics-mv
                            const valueFromInput = textBox.input.value
                                .replace(new RegExp(`[^0-9${textBox.decimalSeparator}]`, 'g'), "")
                                .replace(new RegExp(StringHelper.escapeRegExp(textBox.decimalSeparator), 'g'), '.');

                            if (Number(value) == Number(valueFromInput)) {
                                value = valueFromInput;
                            }
                        }
                        const valueBeforeFormatting = value;
                        let decimal = (value ? (value.split('.')[1] || "") : "").substr(0, textBox.decimalPlaces);
                        const options: Intl.NumberFormatOptions = {
                            maximumFractionDigits: textBox.decimalPlaces,
                            minimumFractionDigits: textBox.readonly || textBox.disabled ? textBox.decimalPlaces : 0,
                            useGrouping: textBox.useNumberGrouping
                        };
                        if (value) {
                            value = Number(value).toFixed(textBox.decimalPlaces);
                            value = Intl.NumberFormat(textBox.culture || window.navigator.language, options).format(value);
                        }

                        const valueAfterFormatting = value
                            .replace(new RegExp(StringHelper.escapeRegExp(textBox.thousandSeparator), 'g'), "")
                            .replace(new RegExp(StringHelper.escapeRegExp(textBox.decimalSeparator), 'g'), '.');

                        if (valueBeforeFormatting && valueBeforeFormatting != valueAfterFormatting) {
                            const index = value.indexOf(textBox.decimalSeparator);
                            value = value + (index === -1 ? textBox.decimalSeparator : "");
                            if (!textBox.isFocused) {
                                let fractionalPart = "";
                                let dec = value.split(textBox.decimalSeparator)[1];
                                if (dec) {
                                    if (dec.length < textBox.decimalPlaces) {
                                        fractionalPart = this.repeat("0", textBox.decimalPlaces - dec.length);
                                    }
                                    dec = dec + fractionalPart;
                                } else {
                                    dec = this.repeat("0", textBox.decimalPlaces);
                                }
                                decimal = dec;
                                value = value + decimal;
                            }
                        }
                        if (value && decimal) {
                            value = value.split(textBox.decimalSeparator)[0] + textBox.decimalSeparator + decimal;
                        }
                        if (value === "NaN" || (value && value.toString().indexOf("NaN") > -1)) {
                            value = "";
                        }
                        if ((sign === "-" || sign === "+") &&
                            value.indexOf(sign) === -1 &&
                            textBox.allowNegative) {
                            value = sign + value;
                        }

                        //If number ends with . like 38. and decimalPlaces is 0 then we need to remove the dot
                        if (value && StringHelper.endsWith(value, textBox.decimalSeparator) && textBox.decimalPlaces == 0) {
                            value = value.substr(0, value.length - 1);
                        }
                    }
                    break;
                case "integer":
                case "digits":
                case "numeric-password":
                    {
                        if (value && value.toString() == "NaN") {
                            value = "";
                        }
                        const options: Intl.NumberFormatOptions = {
                            maximumFractionDigits: 0,
                            useGrouping: textBox.useNumberGrouping
                        };
                        value = this.removeAllButLast(value, textBox.decimalSeparator);
                        value = value ? value.replace(new RegExp(`[^0-9${textBox.decimalSeparator}]`, 'g'), "") : value;
                        if (value) {
                            switch (textBox.type) {
                                case "integer":
                                    value = Number(value).toFixed(0);
                                    break;
                            }
                            value = Intl.NumberFormat(textBox.culture || window.navigator.language, options).format(value);
                        }
                        if (value === "NaN") {
                            value = "";
                        }
                        if ((sign === "-" || sign === "+") &&
                            value.indexOf(sign) === -1 &&
                            textBox.allowNegative) {
                            value = sign + value;
                        }
                    }
                    break;
                default:
                    break;
            }
        }
        return value;
    }
    public fromView(value: any, param: any): string {
        if (value && typeof value !== "string") {
            value = value.toString();
        }
        const textBox: CompTextBox = param.vm as CompTextBox;
        const fromViewValue: string = value;
        let caretIndex: number = null;
        let isNumber: boolean = false;
        const firstChar: string = value && value.length > 0 ? value[0] : "";
        if (textBox) {
            switch (textBox.type) {
                case "double":
                case "float":
                    {
                        caretIndex = textBox.input.selectionStart;
                        value = this.removeAllButLast(value, textBox.decimalSeparator);
                        value = value
                            .replace(new RegExp(`[^0-9${textBox.decimalSeparator}]`, 'g'), "")
                            .replace(new RegExp(StringHelper.escapeRegExp(textBox.decimalSeparator), 'g'), '.');

                        const decimal = value.split(".")[1];

                        if (decimal && decimal.length > textBox.decimalPlaces) {
                            value = Number(value).toFixed(textBox.decimalPlaces);
                        }

                        if ((firstChar === "-" || firstChar === "+") &&
                            value.indexOf(firstChar) === -1 &&
                            textBox.allowNegative) {
                            value = firstChar + value;
                        }
                        isNumber = true;
                        break;
                    }
                case "integer":
                case "digits":
                    caretIndex = textBox.input.selectionStart;
                    value = value.replace(new RegExp(`[^0-9]`, 'g'), "");

                    if ((firstChar === "-" || firstChar === "+") &&
                        value.indexOf(firstChar) === -1 &&
                        textBox.allowNegative) {
                        value = firstChar + value;
                    }
                    isNumber = true;
                    break;
                case "numeric-password":
                    {
                        caretIndex = textBox.input.selectionStart;
                        value = value.replace(new RegExp(`[^0-9${textBox.decimalSeparator}]`, 'g'), "");
                        if ((firstChar === "-" || firstChar === "+") &&
                            value.indexOf(firstChar) === -1 &&
                            textBox.allowNegative) {
                            value = firstChar + value;
                        }
                        isNumber = true;
                        break;
                    }
                case "text":
                    {
                        caretIndex = textBox.input.selectionStart;
                    }
                    break;
                case "email":
                    {
                        return value;
                    }
                default:
                    break;
            }
        }
        if (textBox.filterMode.toLowerCase() === CompTextBox.FilterModes.focus) {
            if (textBox.filter) {
                value = textBox.filter(value);
            }
        }

        if (isNumber) {
            textBox.input.value = this.toView(value, param);
            textBox.diffLength = Math.max(0, textBox.input.value.replace(new RegExp(`[^${textBox.thousandSeparator}]`, 'g'), "").length - textBox.diffLength);
            if (caretIndex != null) {
                caretIndex = caretIndex + Math.min(1, Math.max(0, (fromViewValue.lastIndexOf(textBox.decimalSeparator) == (fromViewValue.length - 1) ? 0 : textBox.diffLength)));
                textBox.input.setSelectionRange(caretIndex, caretIndex);
            }
        }

        if (textBox.nullWhenEmpty && value === "") {
            value = null;
        }
        return isNumber && value && !isNaN(value) ? Number(value) : value;
    }

    public removeAllButLast(str: string, token: string): string {
        const parts: string[] = str ? str.split(token) : [str];
        if (parts[1] === undefined) {
            return str;
        } else {
            return parts.slice(0, -1).join("") + token + parts.slice(-1);
        }
    }
}